import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { memo, useCallback, useEffect } from 'react';

import { SEGMENT_TAGS } from '@/analytics/tag-definitions';
import { useContentMfeToggle } from '@/hooks/feature-flag-hooks/useContentMfeToggle';
import { useCmsContent } from '@/hooks/useCmsContent';
import { useAnalyticsContext } from '@/providers/AnalyticsProvider';
import {
  MEMBERSHIP_DASHBOARD_SELECTION_KEY,
  SIGN_UP_V2_SELECTION_KEY,
} from '@/services/content-a/fetch-general-page';

import styles from './CmsContent.module.scss';
import { CmsMfeZone } from './CmsMfeZone';
import { SignUpButton } from './CompanionComponents/SignUpButton/SignUpButton';
import { CMS_CREATIVE_NAMES, TRY_30_DAY_FREE_TRIAL_BUTTON_ZONE } from './constants';

// Legacy zones pull in @chewy/cms-content-block (CJS) which require()s @chewy/chirp-media-kit (ESM). Load only on client.
const CmsLegacyZones = dynamic(
  () => import('@/components/common/CmsContent/CmsLegacyZones').then(m => m.CmsLegacyZones),
  { ssr: false },
);

interface Props {
  className?: string;
  hasPageLevelZoneSpacing?: boolean;
  hideHeader?: boolean;
  zones?: number[];
  selectionKey?: typeof MEMBERSHIP_DASHBOARD_SELECTION_KEY | typeof SIGN_UP_V2_SELECTION_KEY;
  handleAuthCheckAndConfirmationSheet?: () => void;
}

const getImpressionEvent = (creativeName: string, selectionKey?: string) => {
  if (creativeName === CMS_CREATIVE_NAMES.BenefitsWidget) {
    return SEGMENT_TAGS.ViewsChewyPlusBenefitWidget;
  }
  if (creativeName === CMS_CREATIVE_NAMES.HowItWorksInfoWidget) {
    return SEGMENT_TAGS.ViewsHowChewyPlusWorksInfoWidget;
  }
  if (creativeName === CMS_CREATIVE_NAMES.FaqWidget) {
    return selectionKey === 'membership-dashboard'
      ? SEGMENT_TAGS.ViewsFaqWidgetPetProfile
      : SEGMENT_TAGS.ViewsFaqWidget;
  }
  return null;
};

const getClickEvent = (creativeName: string) => {
  if (creativeName === CMS_CREATIVE_NAMES.BenefitsWidget) {
    return SEGMENT_TAGS.ProceedsToSignupFlyoutBenefits;
  }
  if (creativeName === CMS_CREATIVE_NAMES.EnjoyWidget) {
    return SEGMENT_TAGS.ProceedsToSignupFlyoutInfo;
  }
  return null;
};

export const CmsContent = memo(
  ({
    className,
    hasPageLevelZoneSpacing,
    hideHeader,
    selectionKey,
    zones,
    handleAuthCheckAndConfirmationSheet,
  }: Props) => {
    const isContentMfeEnabled = useContentMfeToggle();
    const { data: cmsData, isLoading, isError } = useCmsContent(!isContentMfeEnabled, selectionKey);

    const { trackSegmentEvent } = useAnalyticsContext();

    const emitZoneAnalyticsImpressionEvent = useCallback(
      (creativeName: string) => {
        const event = getImpressionEvent(creativeName, selectionKey);
        if (event) {
          trackSegmentEvent(event.eventName, event.data);
        }
      },
      [selectionKey, trackSegmentEvent],
    );

    const emitZoneAnalyticsClickEvent = useCallback(
      (creativeName: string) => {
        const event = getClickEvent(creativeName);
        if (event) {
          trackSegmentEvent(event.eventName, event.data);
        }
      },
      [trackSegmentEvent],
    );

    useEffect(() => {
      if (cmsData?.zones) {
        cmsData.zones.forEach(zone => {
          const creativeName = zone?.components[0]?.analytics?.creativeName;
          if (creativeName) emitZoneAnalyticsImpressionEvent(creativeName);
        });
      }
    }, [cmsData?.zones, emitZoneAnalyticsImpressionEvent]);

    const handleSignUpButtonClick = (creativeName?: string) => {
      if (creativeName) {
        emitZoneAnalyticsClickEvent(creativeName);
      }
      handleAuthCheckAndConfirmationSheet?.();
    };

    const renderMfeZones = () => {
      if (!zones || zones.length === 0) {
        return null;
      }

      return zones.map((zoneId: number) => {
        const shouldRenderButton =
          selectionKey === SIGN_UP_V2_SELECTION_KEY && zoneId === TRY_30_DAY_FREE_TRIAL_BUTTON_ZONE;

        return (
          <div
            className={clsx(hasPageLevelZoneSpacing && styles.cmsZone)}
            data-testid={`cms-zone-wrapper-${zoneId}`}
            key={`mfe-zone-${zoneId}`}
          >
            <CmsMfeZone selectionKey={selectionKey} zoneId={zoneId} />
            {shouldRenderButton && (
              <SignUpButton
                handleAction={() => {
                  handleSignUpButtonClick(CMS_CREATIVE_NAMES.EnjoyWidget);
                }}
              />
            )}
          </div>
        );
      });
    };

    return (
      <div
        className={clsx(className, hideHeader && styles.hideHeader)}
        data-testid="cms-universal-zones"
      >
        {isContentMfeEnabled ? (
          renderMfeZones()
        ) : (
          <CmsLegacyZones
            cmsData={cmsData}
            handleSignUpButtonClick={handleSignUpButtonClick}
            hasPageLevelZoneSpacing={hasPageLevelZoneSpacing}
            isError={isError}
            isLoading={isLoading}
            selectionKey={selectionKey}
            zones={zones}
          />
        )}
      </div>
    );
  },
);
